import { useEffect, useMemo, useState } from "react";
import { api, formatMoney, CATEGORY_COLORS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import ProductDialog from "@/components/ProductDialog";
import StockMovementDialog from "@/components/StockMovementDialog";
import { Plus, Package, ArrowDownRight, ArrowUpRight, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [movement, setMovement] = useState({ open: false, type: "IN", productId: "" });

    const load = async () => {
        const [p, c] = await Promise.all([api.get("/products"), api.get("/categories")]);
        setProducts(p.data);
        setCategories(c.data);
    };

    useEffect(() => {
        load();
    }, []);

    const catMap = useMemo(
        () => Object.fromEntries(categories.map((c) => [c.id, c])),
        [categories]
    );

    const filtered = products.filter((p) => {
        const q = search.trim().toLowerCase();
        const matchSearch = !q || p.name.toLowerCase().includes(q);
        const matchCat = categoryFilter === "all" || p.category_id === categoryFilter;
        return matchSearch && matchCat;
    });

    const remove = async (p) => {
        if (!window.confirm(`Supprimer le produit "${p.name}" ?`)) return;
        try {
            await api.delete(`/products/${p.id}`);
            toast.success("Produit supprimé");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
        }
    };

    return (
        <>
            <PageHeader
                title="Inventaire"
                subtitle="Vos produits, quantités et valeurs"
                actions={
                    <Button
                        data-testid="inventory-add-product-btn"
                        onClick={() => {
                            setEditing(null);
                            setDialogOpen(true);
                        }}
                        className="bg-[#3D4E2E] hover:bg-[#2E3A22] text-white"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau produit
                    </Button>
                }
            />

            <div className="p-8 space-y-6">
                <div className="flex gap-3 flex-wrap">
                    <Input
                        data-testid="inventory-search-input"
                        placeholder="Rechercher un produit…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="max-w-sm h-11 bg-white"
                    />
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger
                            data-testid="inventory-category-filter"
                            className="w-56 h-11 bg-white"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes catégories</SelectItem>
                            {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {c.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <Card className="border-slate-200 shadow-sm overflow-hidden">
                    <Table>
                        <TableHeader className="bg-slate-50">
                            <TableRow>
                                <TableHead className="text-slate-600">Produit</TableHead>
                                <TableHead className="text-slate-600">Catégorie</TableHead>
                                <TableHead className="text-right text-slate-600">
                                    Prix d&apos;achat
                                </TableHead>
                                <TableHead className="text-right text-slate-600">
                                    Prix de vente
                                </TableHead>
                                <TableHead className="text-right text-slate-600">Stock</TableHead>
                                <TableHead className="text-right text-slate-600">Valeur</TableHead>
                                <TableHead className="text-right text-slate-600">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={7}
                                        className="text-center text-slate-500 py-10"
                                    >
                                        Aucun produit
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((p) => {
                                const cat = catMap[p.category_id];
                                const color =
                                    CATEGORY_COLORS[cat?.color] || CATEGORY_COLORS.slate;
                                const low = p.quantity <= p.low_stock_threshold;
                                return (
                                    <TableRow
                                        key={p.id}
                                        data-testid={`product-row-${p.id}`}
                                        className="hover:bg-slate-50"
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
                                                    <Package className="w-4 h-4" />
                                                </div>
                                                <div>
                                                    <div className="font-medium text-slate-900">
                                                        {p.name}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        Seuil bas : {p.low_stock_threshold}
                                                    </div>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {cat && (
                                                <Badge className={`${color} border font-medium`}>
                                                    {cat.name}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {formatMoney(p.purchase_price)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            {formatMoney(p.sale_price)}
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            <span
                                                className={
                                                    low
                                                        ? "text-rose-700 font-semibold"
                                                        : "text-slate-900 font-medium"
                                                }
                                            >
                                                {p.quantity}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums font-medium">
                                            {formatMoney(p.quantity * (p.purchase_price ?? 0))}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="inline-flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    data-testid={`product-in-btn-${p.id}`}
                                                    onClick={() =>
                                                        setMovement({
                                                            open: true,
                                                            type: "IN",
                                                            productId: p.id,
                                                        })
                                                    }
                                                    className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                                                >
                                                    <ArrowDownRight className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    data-testid={`product-out-btn-${p.id}`}
                                                    onClick={() =>
                                                        setMovement({
                                                            open: true,
                                                            type: "OUT",
                                                            productId: p.id,
                                                        })
                                                    }
                                                    className="text-rose-700 hover:text-rose-800 hover:bg-rose-50"
                                                >
                                                    <ArrowUpRight className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    data-testid={`product-edit-btn-${p.id}`}
                                                    onClick={() => {
                                                        setEditing(p);
                                                        setDialogOpen(true);
                                                    }}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    data-testid={`product-delete-btn-${p.id}`}
                                                    onClick={() => remove(p)}
                                                    className="text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            <ProductDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                categories={categories}
                editing={editing}
                onDone={load}
            />
            <StockMovementDialog
                open={movement.open}
                onOpenChange={(v) => setMovement((m) => ({ ...m, open: v }))}
                type={movement.type}
                products={products}
                defaultProductId={movement.productId}
                onDone={load}
            />
        </>
    );
}
